import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const defaultLocale = 'en-gb';
const defaultCurrencyCode = 'GBP';
const defaultCurrencyName = 'British Pound';
const defaultCountry = 'GB';
const defaultLanguage = 'en';

export const currencyCodeCache = window.Shopify.currency;

// Create the context
const LocaleContext = createContext({
  locale: defaultLocale,
  changeLocale: /** @type {(newLocale: string) => void} */ (() => {}),
  isLoading: false,
  currencyCode: defaultCurrencyCode,
  currencyName: defaultCurrencyName,
  country: defaultCountry,
  language: defaultLanguage,
  formatPrice: /** @type {({ amount }: { amount: number }) => string} */ (() => '')
});

// Custom hook to use the locale context
export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

// Provider component
export const LocaleProvider = ({ children, defaultLocale = 'en' }) => {
  const [locale, setLocale] = useState(defaultLocale);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyCode, setCurrencyCode] = useState(defaultCurrencyCode);
  const [currencyName, setCurrencyName] = useState(defaultCurrencyName);
  const [country, setCountry] = useState(defaultCountry);
  const [language, setLanguage] = useState(defaultLanguage);

  // Load locale from localStorage on mount
  useEffect(() => {
    if (window?.Shopify?.currency.active) {
      const currency = window.Shopify.currency.active;
      setCurrencyCode(currency);
      setCurrencyName(currency);
    }
    if (window?.Shopify?.country) {
      const country = window.Shopify.country;
      const language = window.Shopify.locale;
      setCountry(country);
      setLanguage(language);
      setLocale(`${language}-${country.toLowerCase()}`);
    }
  }, []);

  const changeLocale = useCallback((newLocale) => {
    setIsLoading(true);
    setLocale(newLocale);
    setIsLoading(false);
  }, []);

  /**
   * Formats a number to a price with the given currency symbol and two decimal places.
   * @param {Object} options - The options for formatting.
   * @param {number} options.amount - The amount to format.
   * @returns {string} The formatted price string (e.g. '£12.99').
   */
  const formatPrice = useCallback(
    ({ amount }) => {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(amount);
    },
    [locale, currencyCode]
  );

  const value = {
    locale,
    changeLocale,
    isLoading,
    currencyCode,
    currencyName,
    country,
    language,
    formatPrice
  };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

// Export the context for direct usage if needed
export default LocaleContext;
